import React, { useState, useEffect, useCallback } from 'react';
import avulsaService from '../services/avulsaService';
import NCEditor from './NCEditor';
import Button from './ui/Button';
import './Login.css';

/**
 * Análise Avulsa V1 — resultado (PR4d + PR-B editor).
 *
 * O comprador vê a investigação completa que pagou, pode EDITAR a análise (ações
 * corretivas, causa raiz, porquês, 6M, conclusão, impacto, hipótese, eficácia,
 * contenção, 5W2H, preventivas) e baixar/imprimir o PDF.
 *
 * Duas superfícies:
 *  • "Ver relatório" (default): renderiza o relatorio_html (report_service V1, que
 *    JÁ traz o rodapé avulsa do PR3b) — o ENTREGÁVEL real — num iframe sandbox.
 *  • "Editar análise": reusa o NCEditor do app (mesmo editor, byte-idêntico) com a
 *    persistência injetada (avulsaService.editarAnalise → PUT /api/avulsa/nc/{id}/
 *    editar-analise, do PR-A). O comprador é admin do tenant dono da NC, então o
 *    mesmo _editar_analise_v4_impl roda. allowIaRecalc=false (recalcular com IA no
 *    avulsa é o "rerun"); allowBaseFieldEdit=false (os campos de input — contexto/
 *    indústria/custo — não têm endpoint avulsa; mudá-los é o rerun, que re-roda a
 *    análise inteira). Após salvar, recarrega a NC → o relatório reflete a edição.
 *
 * Janela: edição liberada por 7 dias (workspace_expires_at); depois a compra fica
 * "frozen" (read-only) — o backend 409 e a UI esconde o botão de editar. O PDF
 * baixado é permanente; o acesso online fica 30 dias.
 *
 * Bússola GTM: a avulsa resolve uma RNC; a assinatura gere a qualidade inteira
 * (prazos, eficácia, histórico) → upsell honesto ao pé do resultado.
 *
 * #257 — seletor de seções do export: checkboxes livres que escolhem o que entra
 * no relatório/PDF (versão pro gestor, auditor ou cliente). Re-busca o getNc com
 * `?secoes=` → preview + PDF refletem (WYSIWYG). 'identificacao' é sempre incluída
 * (backend força) → não é checkbox.
 */
// Seções filtráveis (avulsa = sempre Investigação → todas se aplicam; seção sem
// conteúdo simplesmente não aparece). Chaves = as do report_service (#257 PR-A).
const SECOES_EXPORT = [
  { key: 'descricao', label: 'Descrição da NC' },
  { key: 'contencao', label: 'Ação de contenção imediata' },
  { key: 'porques', label: 'Análise dos 5 Porquês' },
  { key: 'analise_6m', label: 'Análise 6M (Ishikawa)' },
  { key: 'causa_raiz', label: 'Declaração da causa raiz' },
  { key: 'hipotese_alternativa', label: 'Hipótese alternativa' },
  { key: 'acoes', label: 'Plano de ação' },
  { key: 'gantt', label: 'Cronograma (Gantt)' },
  { key: 'conclusao', label: 'Conclusão executiva' },
];
const _todasSecoesSel = () => SECOES_EXPORT.reduce((acc, s) => { acc[s.key] = true; return acc; }, {});
// null quando TODAS marcadas (= relatório completo, sem param `secoes`); senão a lista marcada.
function _secoesAtivas(sel) {
  const marcadas = SECOES_EXPORT.filter((s) => sel[s.key]).map((s) => s.key);
  return marcadas.length === SECOES_EXPORT.length ? null : marcadas;
}

function AvulsaResult({ numeroNc, onRepurchase }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repurchasing, setRepurchasing] = useState(false);
  const [repurchaseError, setRepurchaseError] = useState('');
  const [html, setHtml] = useState('');
  const [numero, setNumero] = useState(numeroNc || null);
  const [ncData, setNcData] = useState(null); // payload completo do GET /api/nc/{id}
  const [ncId, setNcId] = useState(null);
  const [frozen, setFrozen] = useState(false); // janela de 7d expirou → read-only
  const [editMode, setEditMode] = useState(false);
  const [secoesSel, setSecoesSel] = useState(_todasSecoesSel); // #257: todas marcadas por default
  const [mostrarSecoes, setMostrarSecoes] = useState(false);   // painel do seletor (recolhido)

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const ws = await avulsaService.workspaceStatus();
      if (!ws.nc_id) {
        setError('Ainda não encontramos sua análise. Recarregue em instantes.');
        return;
      }
      setNcId(ws.nc_id);
      setFrozen(!!ws.frozen);
      const nc = await avulsaService.getNc(ws.nc_id);
      setNcData(nc);
      setHtml(nc.relatorio_html || '');
      if (nc.numero_nc) setNumero(nc.numero_nc);
      if (!nc.relatorio_html) {
        setError('O relatório ainda está sendo finalizado. Recarregue em instantes.');
      }
    } catch (err) {
      if (err.status === 401) {
        avulsaService.clearSession();
        window.location.assign('/avulsa');
        return;
      }
      setError(err.message || 'Não foi possível carregar sua análise. Tente recarregar.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Pós-salvar no editor: recarrega a NC (o relatorio_html é regenerado on-read
  // pelo backend → o relatório reflete a edição). Sem recarregar a página.
  const handleEditorSave = useCallback(async () => {
    if (!ncId) return;
    try {
      // mantém a seleção de seções ativa (#257) no relatório regenerado
      const nc = await avulsaService.getNc(ncId, { secoes: _secoesAtivas(secoesSel) || undefined });
      setNcData(nc);
      setHtml(nc.relatorio_html || '');
    } catch (err) {
      // erro de refresh não derruba o editor — a edição já foi persistida
      console.error('Falha ao recarregar a NC após salvar:', err);
    }
  }, [ncId, secoesSel]);

  // #257 — re-busca o relatório filtrado pelas seções marcadas (preview + PDF WYSIWYG).
  const aplicarSecoes = async (sel) => {
    if (!ncId) return;
    try {
      const nc = await avulsaService.getNc(ncId, { secoes: _secoesAtivas(sel) || undefined });
      setHtml(nc.relatorio_html || '');
    } catch (err) {
      console.error('Falha ao filtrar seções do relatório:', err);
    }
  };
  const toggleSecao = (key) => {
    setSecoesSel((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      aplicarSecoes(next);
      return next;
    });
  };

  // Imprime/salva como PDF o relatório já carregado no cliente (sem token na URL).
  const handlePrint = () => {
    if (!html) return;
    avulsaService.event('pdf_downloaded');
    const w = window.open('', '_blank');
    if (!w) {
      setError('Não foi possível abrir a janela de impressão — verifique se o navegador bloqueou pop-ups.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* usuário imprime manualmente */ } }, 600);
  };

  // #297 — recompra: inicia uma NOVA análise (nova compra) → o AvulsaPage volta
  // pro fill. O backend só permite a partir de um estado terminal; o pai é quem
  // reavalia a fase (onRepurchase).
  const handleRepurchase = async () => {
    setRepurchaseError('');
    setRepurchasing(true);
    try {
      await avulsaService.repurchase();
      avulsaService.event('subscribe_clicked', { acao: 'recompra' });
      if (onRepurchase) onRepurchase();
    } catch (err) {
      setRepurchaseError(err.message || 'Não foi possível iniciar uma nova análise agora. Tente de novo.');
      setRepurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page-wrapper">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#10b981', animation: 'spin 1s linear infinite' }}>progress_activity</span>
          <p className="form-subtitle">Carregando sua análise…</p>
        </div>
      </div>
    );
  }

  // O editor só faz sentido com a análise carregada e a janela aberta (não-frozen).
  const podeEditar = !!ncId && !!ncData?.analise_v4 && !frozen;

  return (
    <div className="login-page-wrapper" style={{ alignItems: 'flex-start', padding: '32px 16px', overflowY: 'auto' }}>
      <div style={{ width: '100%', maxWidth: editMode ? 1040 : 920, margin: '0 auto' }}>
        {/* Cabeçalho + ações principais */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#10b981' }}>verified</span>
          <h2 className="form-title" style={{ marginTop: 6 }}>Sua análise está pronta</h2>
          <p className="form-subtitle" style={{ maxWidth: 560, margin: '6px auto 0' }}>
            {numero ? <>Investigação completa da <strong>{numero}</strong>.</> : 'Investigação completa.'}{' '}
            O documento é seu — baixe o PDF e leve pra onde precisar.
            {podeEditar && ' Quer ajustar algo? Você pode editar a análise por 7 dias.'}
          </p>
          <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {!editMode && (
              <Button type="button" onClick={handlePrint} disabled={!html}>
                <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>download</span>
                Baixar / imprimir PDF
              </Button>
            )}
            {podeEditar && (
              <Button
                type="button"
                variant={editMode ? 'primary' : 'secondary'}
                onClick={() => setEditMode((v) => !v)}
              >
                <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>
                  {editMode ? 'visibility' : 'edit'}
                </span>
                {editMode ? 'Ver relatório' : 'Editar análise'}
              </Button>
            )}
          </div>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Modo EDITAR — reusa o NCEditor do app com persistência avulsa injetada */}
        {editMode && ncData && (
          <div style={{
            border: '1px solid #e2e8f0', borderRadius: 10,
            background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '16px',
          }}>
            <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px', lineHeight: 1.5 }}>
              Edite o que precisar — <strong>causa raiz, porquês, ações corretivas, 6M</strong> e
              demais campos da análise. Cada campo salva na hora. As mudanças entram no
              relatório (volte em <strong>"Ver relatório"</strong> e baixe o PDF atualizado).
            </p>
            <NCEditor
              ncId={ncId}
              analise={ncData.analise}
              analiseV4={ncData.analise_v4}
              nc={ncData}
              onSave={handleEditorSave}
              editarAnaliseFn={avulsaService.editarAnalise}
              allowIaRecalc={false}
              allowBaseFieldEdit={false}
            />
          </div>
        )}

        {/* #257 — seletor de seções do export (só no modo de leitura) */}
        {!editMode && html && (
          <div style={{ marginBottom: 14, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
            <button
              type="button"
              onClick={() => setMostrarSecoes((v) => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: '#334155' }}
            >
              <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#10b981' }}>tune</span>
                Seções do relatório
              </span>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {SECOES_EXPORT.filter((s) => secoesSel[s.key]).length}/{SECOES_EXPORT.length} · {mostrarSecoes ? 'ocultar' : 'escolher'}
              </span>
            </button>
            {mostrarSecoes && (
              <div style={{ padding: '0 16px 16px' }}>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>
                  Marque o que entra no relatório e no PDF — útil pra montar uma versão pro gestor,
                  pro auditor ou pro cliente. A <strong>Identificação</strong> é sempre incluída.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                  {SECOES_EXPORT.map((s) => (
                    <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!secoesSel[s.key]} onChange={() => toggleSecao(s.key)} style={{ width: 'auto' }} />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modo VER — relatório (entregável real, com o rodapé avulsa) num iframe isolado */}
        {!editMode && html && (
          <div style={{
            border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
            background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <iframe
              title="Relatório da análise"
              srcDoc={html}
              sandbox=""
              style={{ width: '100%', height: '78vh', border: 'none', display: 'block' }}
            />
          </div>
        )}

        {/* Aviso de janela fechada (read-only após 7 dias) */}
        {frozen && !editMode && (
          <p style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginTop: 16 }}>
            O período de edição (7 dias) desta análise encerrou. O relatório segue disponível
            para leitura e download. Para continuar gerindo esta NC — editar, acompanhar prazos
            e eficácia — assine um plano.
          </p>
        )}

        {/* Upsell honesto (bússola: evento × processo) — só no modo de leitura */}
        {!editMode && (
          <div className="feature-card-primary" style={{ marginTop: 24 }}>
            <div className="feature-primary-content">
              <h3 className="feature-primary-title">Esta análise resolveu uma RNC. E as próximas?</h3>
              <p className="feature-primary-description">
                A análise pontual entrega o diagnóstico. Para acompanhar <strong>prazos</strong>,
                verificar a <strong>eficácia</strong> das ações (exigência ISO) e manter o
                <strong> histórico</strong> das suas não-conformidades, a assinatura cuida da
                qualidade inteira — e seu pagamento de hoje pode virar crédito.
              </p>
              <div style={{ marginTop: 14 }}>
                <a href="https://qaasa.com.br/planos" target="_blank" rel="noopener noreferrer"
                  className="forgot-link" style={{ fontWeight: 600 }}
                  onClick={() => avulsaService.event('subscribe_clicked')}>Conhecer os planos →</a>
              </div>
            </div>
            <div className="feature-primary-icon">
              <span className="material-symbols-outlined">trending_up</span>
            </div>
          </div>
        )}

        {/* #297 — recompra: analisar outra NC (pontual, sem assinar) */}
        {!editMode && onRepurchase && (
          <div style={{
            marginTop: 16, padding: '16px 18px', border: '1px solid #e2e8f0',
            borderRadius: 10, background: '#f8fafc', textAlign: 'center',
          }}>
            <p className="form-subtitle" style={{ margin: '0 0 12px' }}>
              Tem outra não-conformidade pra investigar? Rode uma <strong>nova análise pontual</strong> agora —
              sem assinar.
            </p>
            <Button type="button" variant="secondary" onClick={handleRepurchase} disabled={repurchasing}>
              <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>add_circle</span>
              {repurchasing ? 'Iniciando…' : 'Analisar outra NC (R$75)'}
            </Button>
            {repurchaseError && (
              <div className="error-message" style={{ marginTop: 12 }}>{repurchaseError}</div>
            )}
          </div>
        )}

        <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', margin: '20px 0 8px' }}>
          Guarde seu PDF — ele é seu pra sempre. O acesso online a esta análise fica disponível por 30 dias.
        </p>
      </div>
    </div>
  );
}

export default AvulsaResult;
